/* global ResizeObserver */
// ============================================================
// useDrum - the Armoury drum's behaviour: the cursor, the swing to the
// armed weapon, the rotation layout, the wheel, and focus following.
//
// Split out of `ArmoryTab` so the tab reads as what it renders. The
// arithmetic is in `drum.ts`; this file is only the React wiring
// (state, refs, effects) around it.
// ============================================================
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction, WheelEvent } from 'react';
import { circularOffset, ringStep, stackPositions, wheelNotch, wheelPixels } from './drum';

/** Longer than a double-click gap, shorter than feeling laggy. */
const SWING_DELAY_MS = 350;

type Plates = MutableRefObject<(HTMLButtonElement | null)[]>;
type Navigate = (next: number | ((c: number) => number)) => void;

interface CursorInput {
  selectedSlot: number | null;
  count: number;
  ring: number[];
  searching: boolean;
}

/**
 * THE DRUM'S CENTRE IS THE CURSOR - where you are LOOKING, which is a
 * different thing from what you are HOLDING. If the centre were pinned
 * to the armed weapon, turning the drum to look at another gun would be
 * impossible without arming it. So: the drum turns freely (wheel,
 * arrows, click), the armed weapon keeps its gold spine wherever it
 * rotates to, and ARMING a weapon swings it into the centre.
 *
 * Starts ON the armed weapon: at mount there is no click to race, so
 * the drum opens already centred on what is in hand.
 */
export function useDrumCursor({ selectedSlot, count, ring, searching }: CursorInput) {
  const [cursor, setCursor] = useState<number>(() => initialCursor(selectedSlot));
  const swingTimer = useSwingToArmed(selectedSlot, count, setCursor);
  useCursorBounds(count, ring, searching, setCursor);

  /**
   * Navigate - and CANCEL any pending arm-swing doing so.
   *
   * The deferral that protects double-clicks created a yank: arm, wheel
   * onward within 350ms, and the stale timer fired and snapped the drum
   * back to the armed weapon, eating the notch. Caught by the full sweep
   * (54px drift = exactly one plate). Once the user navigates by hand,
   * they have asserted control; the pending swing is void.
   */
  const navigate: Navigate = (next) => {
    if (swingTimer.current) clearTimeout(swingTimer.current);
    setCursor(next);
  };

  return { cursor, navigate };
}

/** The armed weapon's position, or the first plate when nothing is armed. */
function initialCursor(selectedSlot: number | null): number {
  return selectedSlot !== null && selectedSlot >= 0 ? selectedSlot : 0;
}

/** Keep the cursor on a plate that exists and is showing. */
function useCursorBounds(
  count: number,
  ring: number[],
  searching: boolean,
  setCursor: Dispatch<SetStateAction<number>>,
) {
  // A shrinking rack cannot leave the cursor pointing past its end.
  useEffect(() => {
    setCursor((c) => Math.min(c, Math.max(0, count - 1)));
  }, [count, setCursor]);

  // A type chip that hides the cursor's plate snaps it to the nearest
  // visible chamber - a drum centred on an invisible weapon turns blind.
  const ringKey = ring.join(',');
  useEffect(() => {
    if (searching || ring.length === 0) return;
    setCursor((c) => (ring.includes(c) ? c : ring[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringKey, searching]);
}

/**
 * Arming - by click, hotbar, the 1-9 keys or a macro - swings the drum
 * to the weapon now in hand, AFTER the double-click window has closed.
 *
 * The swing used to fire on the FIRST click of a double-click, so the
 * plate rotated out from under the second click and the editor opened
 * on whatever moved into its place - the ammo edit landed on the wrong
 * weapon. Caught by a Playwright probe whose dblclick raced the
 * rotation exactly the way a human's does. Returns the pending timer,
 * so navigating by hand can cancel it.
 */
function useSwingToArmed(selectedSlot: number | null, count: number, setCursor: (c: number) => void) {
  const swingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (selectedSlot === null || selectedSlot < 0 || selectedSlot >= count) return;
    swingTimer.current = setTimeout(() => setCursor(selectedSlot), SWING_DELAY_MS);
    return () => {
      if (swingTimer.current) clearTimeout(swingTimer.current);
    };
  }, [selectedSlot, count, setCursor]);
  return swingTimer;
}

interface LayoutInput {
  viewportRef: MutableRefObject<HTMLDivElement | null>;
  plates: Plates;
  ring: number[];
  cursor: number;
  searching: boolean;
  count: number;
  selectedSlot: number | null;
}

/**
 * Write each row's `--y`, and suppress the transition of a row that
 * crossed the SEAM (its offset flipped sign the long way round):
 * animating it would fly it through the whole stack; invisible
 * relocation reads as the drum simply having turned.
 */
function placeRows(
  rows: (HTMLElement | null)[],
  { ring, offsets, y }: { ring: number[]; offsets: number[]; y: number[] },
  prev: Record<number, number>,
) {
  rows.forEach((row, k) => {
    if (!row) return;
    const idx = ring[k];
    const wrapped = prev[idx] !== undefined && Math.abs(offsets[k] - prev[idx]) > ring.length / 2;
    row.style.transition = wrapped ? 'none' : '';
    row.style.setProperty('--y', `${Math.round(y[k])}px`);
    prev[idx] = offsets[k];
  });
}

/**
 * Place every row by its CIRCULAR offset from the cursor.
 *
 * The column-translate version could not wrap: a flex column has a top
 * and a bottom, and a cylinder has neither. So each row is positioned
 * absolutely - the centre plate at the middle of the viewport, the rest
 * stacked outward by their measured heights in both directions, and the
 * wrap is thereby VISIBLE: with the cursor on the first weapon, the
 * last one hangs one step above it.
 *
 * Heights are measured, not assumed - the armed plate is taller (it
 * carries the magazine and the full special list). `offsetHeight` and
 * `--y` ignore the scale transform, so measurement is stable.
 *
 * While SEARCHING the drum parks flat (static flow, handled by the
 * `.flat` class) - the visible plates are a subset with their real
 * indices, and a search result list reads better from the top. Under a
 * type chip the cylinder is the visible subset (v8.199.10).
 *
 * NOT WHILE HIDDEN. On the rails the rack lives in the actor panel, which
 * stays mounted while closed; measured there, every height read 0 and each
 * plate stood on the armed one, which alone took a click (2026-10-23). A rack
 * that is not laid out keeps its places, and the viewport's observer places
 * the rows again the moment it is.
 */
export function useDrumLayout({ viewportRef, plates, ring, cursor, searching, count, selectedSlot }: LayoutInput) {
  const prevOffsets = useRef<Record<number, number>>({});
  // The latest placing, for the observer, which outlives the render that made it.
  const place = useRef(() => {});
  const ringKey = ring.join(',');
  useLayoutEffect(() => {
    place.current = () =>
      placeDrum(viewportRef.current, { plates: plates.current, ring, cursor, searching }, prevOffsets);
    place.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, searching, ringKey, count, selectedSlot]);
  usePlaceOnResize(viewportRef, place);
}

interface DrumState {
  plates: (HTMLButtonElement | null)[];
  ring: number[];
  cursor: number;
  searching: boolean;
}

/** One placing of every row; nothing while the rack is not laid out (its panel shut: no height at all). */
function placeDrum(
  viewport: HTMLDivElement | null,
  { plates, ring, cursor, searching }: DrumState,
  prev: MutableRefObject<Record<number, number>>,
) {
  if (!viewport || searching || ring.length === 0) {
    prev.current = {};
    return;
  }
  if (viewport.clientHeight === 0) return;
  const rows = ring.map((idx) => plates[idx]?.parentElement as HTMLElement | null);
  const heights = rows.map((r) => r?.offsetHeight ?? 0);
  const offsets = ring.map((idx) => circularOffset(ring, cursor, idx));
  const centrePos = Math.max(0, ring.indexOf(cursor));
  const y = stackPositions(offsets, heights, centrePos, viewport.clientHeight);
  placeRows(rows, { ring, offsets, y }, prev.current);
}

/** Place the rows again whenever the viewport's box changes - above all when its panel opens. */
function usePlaceOnResize(viewportRef: MutableRefObject<HTMLDivElement | null>, place: MutableRefObject<() => void>) {
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => place.current());
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [viewportRef, place]);
}

/**
 * The wheel turns the drum, one weapon per notch - it does NOT scroll,
 * because there is nothing to scroll, and it does NOT arm, because
 * Space fires whatever is armed and browsing must never change that.
 * Wraps within the ring: past the last visible chamber is the first.
 */
export function useDrumWheel(ring: number[], searching: boolean, navigate: Navigate) {
  const acc = useRef({ total: 0, last: 0 });
  return (e: WheelEvent) => {
    if (searching || ring.length === 0) return;
    const out = wheelNotch(acc.current, wheelPixels(e.deltaY, e.deltaMode), Date.now());
    acc.current = out.acc;
    if (out.step !== 0) navigate((c) => ringStep(ring, c, out.step));
  };
}

/**
 * Follow the cursor with real focus, so the arrows move a screen
 * reader's attention rather than only a highlight (CombatantGallery) -
 * but ONLY when focus is already inside the rack. The wheel also turns
 * the drum, and a wheel over a panel must not yank focus out of the
 * search box or the editor.
 */
export function useFocusFollowsCursor(plates: Plates, cursor: number, count: number) {
  useEffect(() => {
    const el = plates.current[cursor];
    if (!el) return;
    const list = el.closest('[role="listbox"]');
    if (list && list.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, [plates, cursor, count]);
}
